

import React, { useEffect, useMemo, useRef, useState } from "react";
import ProductSearchOverlay from "../pages/shared/ProductSearchOverlay";
import { Link, useLocation } from "react-router-dom";
import {
  normalizeNavTree,
  collectNavNodes,
  matchesNavPath,
  getNavItemId
} from "../pages/storefront/storefrontData";
import { STOREFRONT_LANGUAGE_OPTIONS, t } from "../i18n";


export default function StorefrontHeader({
  storefrontTitle = "LOYA SPORT",
  logoSrc = "/assets/about/logo.png",
  logoAlt = "Loya Sport logo",
  navItems = [],
  language = "en",
  currency = "USD",
  onChangeLanguage,
  onChangeCurrency,
  effectiveShowPricing = true,
  isAuthenticated = false,
}) {
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [productsMenuOpen, setProductsMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const productsMenuCloseTimerRef = useRef(null);
  const languageMenuRef = useRef(null);
  const productsMegaDragRef = useRef({
    dragging: false,
    startX: 0,
    startScrollLeft: 0
  });

  useEffect(() => {
    return () => {
      if (productsMenuCloseTimerRef.current) {
        clearTimeout(productsMenuCloseTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setProductsMenuOpen(false);
    setLanguageMenuOpen(false);
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (!languageMenuRef.current || languageMenuRef.current.contains(event.target)) {
        return;
      }

      setLanguageMenuOpen(false);
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setLanguageMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  function cancelProductsMenuClose() {
    if (productsMenuCloseTimerRef.current) {
      clearTimeout(productsMenuCloseTimerRef.current);
      productsMenuCloseTimerRef.current = null;
    }
  }

  function openProductsMenu() {
    cancelProductsMenuClose();
    setProductsMenuOpen(true);
  }

  function scheduleProductsMenuClose() {
    cancelProductsMenuClose();
    productsMenuCloseTimerRef.current = setTimeout(() => {
      setProductsMenuOpen(false);
      productsMenuCloseTimerRef.current = null;
    }, 180);
  }

  function handleProductsMenuBlur(event) {
    const nextFocused = event.relatedTarget;
    if (event.currentTarget && nextFocused && event.currentTarget.contains(nextFocused)) {
      return;
    }
    scheduleProductsMenuClose();
  }

  function handleProductsMegaWheel(event) {
    const container = event.currentTarget;
    const canScrollHorizontally = container.scrollWidth > container.clientWidth + 2;
    if (!canScrollHorizontally) {
      return;
    }

    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
      event.preventDefault();
      container.scrollLeft += event.deltaY;
    }
  }

  function handleProductsMegaMouseDown(event) {
    const container = event.currentTarget;
    const canScrollHorizontally = container.scrollWidth > container.clientWidth + 2;
    if (!canScrollHorizontally) {
      return;
    }

    productsMegaDragRef.current = {
      dragging: true,
      startX: event.clientX,
      startScrollLeft: container.scrollLeft
    };
    container.classList.add("is-dragging");
  }

  function handleProductsMegaMouseMove(event) {
    const container = event.currentTarget;
    if (!productsMegaDragRef.current.dragging) {
      return;
    }

    const distance = event.clientX - productsMegaDragRef.current.startX;
    container.scrollLeft = productsMegaDragRef.current.startScrollLeft - distance;
    event.preventDefault();
  }

  function stopProductsMegaDrag(event) {
    const container = event.currentTarget;
    if (!productsMegaDragRef.current.dragging) {
      return;
    }

    productsMegaDragRef.current.dragging = false;
    container.classList.remove("is-dragging");
  }

  function buildGroupedNavItems(items) {
    if (!Array.isArray(items) || !items.length) {
      return [];
    }
    return items;
  }

  const groupedNavItems = useMemo(() => buildGroupedNavItems(navItems), [navItems]);
  const activeNavId = useMemo(() => {
    const flatItems = collectNavNodes(groupedNavItems);
    const matchedItem = flatItems.find(({ item }) => matchesNavPath(location, item.path));
    return matchedItem ? getNavItemId(matchedItem.item, matchedItem.index) : "";
  }, [groupedNavItems, location]);

  function getVisibleChildren(item) {
    if (!Array.isArray(item?.children)) {
      return [];
    }
    return item.children.filter((child) => child?.visible !== false);
  }

  function isNavItemActive(item, fallbackIndex = "0") {
    if (!item || !activeNavId) {
      return false;
    }
    return getNavItemId(item, fallbackIndex) === activeNavId;
  }

  function isNavBranchActive(item, fallbackIndex = "0") {
    if (!item) {
      return false;
    }

    return (
      isNavItemActive(item, fallbackIndex) ||
      collectNavNodes(item.children, fallbackIndex).some(({ item: child, index: childIndex }) =>
        isNavItemActive(child, childIndex)
      )
    );
  }

  function dismissNavFocus() {
    if (typeof document === "undefined") {
      return;
    }
    const activeElement = document.activeElement;
    if (activeElement && typeof activeElement.blur === "function") {
      activeElement.blur();
    }
  }

  function handleNavClick(event, path) {
    if (!path || !path.startsWith("/#")) {
      return;
    }
    if (location.pathname !== "/") {
      return;
    }
    const targetId = path.slice(2);
    const target = document.getElementById(targetId);
    if (!target) {
      return;
    }
    event.preventDefault();
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", path);
  }

  function renderNavLink(item, indexPath, className = "") {
    const active = isNavItemActive(item, indexPath);
    const nextClassName = [className, active ? "active" : ""].filter(Boolean).join(" ");

    return (
      <Link
        to={item.path}
        className={nextClassName}
        onClick={(event) => {
          dismissNavFocus();
          setMobileMenuOpen(false);
          setProductsMenuOpen(false);
          handleNavClick(event, item.path);
        }}
      >
        {item.label}
      </Link>
    );
  }

  function renderLanguageFlagIcon(locale) {
    const code = String(locale || "").toLowerCase();

    switch (code) {
      case "en":
        return (
          <svg viewBox="0 0 28 20" role="img" aria-hidden="true">
            <rect width="28" height="20" rx="2" fill="#ffffff" />
            <rect y="0" width="28" height="2" fill="#b22234" />
            <rect y="4" width="28" height="2" fill="#b22234" />
            <rect y="8" width="28" height="2" fill="#b22234" />
            <rect y="12" width="28" height="2" fill="#b22234" />
            <rect y="16" width="28" height="2" fill="#b22234" />
            <rect width="12" height="10" rx="1" fill="#3c3b6e" />
          </svg>
        );
      case "zh":
        return (
          <svg viewBox="0 0 28 20" role="img" aria-hidden="true">
            <rect width="28" height="20" rx="2" fill="#de2910" />
            <polygon points="6,3 7,6 10,6 7.6,7.8 8.6,10.5 6,8.8 3.4,10.5 4.4,7.8 2,6 5,6" fill="#ffde00" />
          </svg>
        );
      case "es":
        return (
          <svg viewBox="0 0 28 20" role="img" aria-hidden="true">
            <rect width="28" height="20" rx="2" fill="#aa151b" />
            <rect y="5" width="28" height="10" fill="#f1bf00" />
          </svg>
        );
      case "pt":
        return (
          <svg viewBox="0 0 28 20" role="img" aria-hidden="true">
            <rect width="11" height="20" rx="2" fill="#046a38" />
            <rect x="11" width="17" height="20" rx="2" fill="#da291c" />
            <circle cx="11" cy="10" r="3" fill="#ffcd00" />
          </svg>
        );
      case "fr":
        return (
          <svg viewBox="0 0 28 20" role="img" aria-hidden="true">
            <rect width="28" height="20" rx="2" fill="#ffffff" />
            <rect width="9.34" height="20" rx="2" fill="#0055a4" />
            <rect x="18.66" width="9.34" height="20" rx="2" fill="#ef4135" />
          </svg>
        );
      case "ja":
        return (
          <svg viewBox="0 0 28 20" role="img" aria-hidden="true">
            <rect width="28" height="20" rx="2" fill="#ffffff" />
            <circle cx="14" cy="10" r="5" fill="#bc002d" />
          </svg>
        );
      case "ko":
        return (
          <svg viewBox="0 0 28 20" role="img" aria-hidden="true">
            <rect width="28" height="20" rx="2" fill="#ffffff" />
            <circle cx="14" cy="10" r="4.6" fill="#cd2e3a" />
            <path d="M9.4 10a4.6 4.6 0 0 0 9.2 0Z" fill="#0047a0" />
            <circle cx="14" cy="8.6" r="1.45" fill="#0047a0" />
            <circle cx="14" cy="11.4" r="1.45" fill="#cd2e3a" />
            <rect x="4" y="3" width="3.7" height="0.9" rx="0.45" fill="#111111" />
            <rect x="4" y="4.5" width="3.7" height="0.9" rx="0.45" fill="#111111" />
            <rect x="4" y="6" width="3.7" height="0.9" rx="0.45" fill="#111111" />
            <rect x="20.3" y="13" width="3.7" height="0.9" rx="0.45" fill="#111111" />
            <rect x="20.3" y="14.5" width="3.7" height="0.9" rx="0.45" fill="#111111" />
            <rect x="20.3" y="16" width="3.7" height="0.9" rx="0.45" fill="#111111" />
          </svg>
        );
      case "ar":
        return (
          <svg viewBox="0 0 28 20" role="img" aria-hidden="true">
            <rect width="28" height="20" rx="2" fill="#006c35" />
            <rect x="6" y="14" width="16" height="1.6" rx="0.8" fill="#ffffff" />
            <circle cx="20.8" cy="14.8" r="1" fill="#ffffff" />
          </svg>
        );
      case "de":
        return (
          <svg viewBox="0 0 28 20" role="img" aria-hidden="true">
            <rect width="28" height="6.67" rx="2" fill="#000000" />
            <rect y="6.67" width="28" height="6.67" fill="#dd0000" />
            <rect y="13.34" width="28" height="6.66" rx="2" fill="#ffce00" />
          </svg>
        );
      case "ru":
        return (
          <svg viewBox="0 0 28 20" role="img" aria-hidden="true">
            <rect width="28" height="6.67" rx="2" fill="#ffffff" />
            <rect y="6.67" width="28" height="6.67" fill="#0039a6" />
            <rect y="13.34" width="28" height="6.66" rx="2" fill="#d52b1e" />
          </svg>
        );
      default:
        return (
          <svg viewBox="0 0 28 20" role="img" aria-hidden="true">
            <rect width="28" height="20" rx="2" fill="#d7dfef" />
          </svg>
        );
    }
  }

  function flattenProductsColumnLinks(items, parentIndex = "") {
    if (!Array.isArray(items) || !items.length) {
      return [];
    }

    return items
      .filter((item) => item?.visible !== false)
      .flatMap((item, index) => {
        const indexPath = parentIndex ? `${parentIndex}-${index}` : String(index);
        const visibleChildren = getVisibleChildren(item);
        if (!visibleChildren.length) {
          return [{ item, indexPath }];
        }

        return [{ item, indexPath }, ...flattenProductsColumnLinks(visibleChildren, indexPath)];
      });
  }

  function renderProductsPanel(item, indexPath) {
    const visibleChildren = getVisibleChildren(item);
    const searchParams = new URLSearchParams(location.search || "");
    const isOthersActive =
      location.pathname === "/products" &&
      String(searchParams.get("group") || "").trim().toLowerCase() === "others";

    return (
      <div
        className="commercehub-submenu commercehub-products-panel commercehub-products-mega-panel"
        role="menu"
        aria-label={`${item.label} submenu`}
        onMouseEnter={openProductsMenu}
        onMouseLeave={scheduleProductsMenuClose}
      >
        <div
          className="commercehub-products-mega-scroll"
          onWheel={handleProductsMegaWheel}
          onMouseDown={handleProductsMegaMouseDown}
          onMouseMove={handleProductsMegaMouseMove}
          onMouseUp={stopProductsMegaDrag}
          onMouseLeave={stopProductsMegaDrag}
        >
          <div className="commercehub-products-mega-grid">
            {visibleChildren.map((child, childIndex) => {
              const childIndexPath = `${indexPath}-${childIndex}`;
              const columnActive = isNavBranchActive(child, childIndexPath);
              const columnLinks = flattenProductsColumnLinks(getVisibleChildren(child), childIndexPath);

              return (
                <div key={child.key || childIndexPath} className={`commercehub-products-column${columnActive ? " active" : ""}`}>
                  <h4 className="commercehub-products-column-title">{child.label}</h4>
                  <div className="commercehub-products-column-links">
                    {columnLinks.length
                      ? columnLinks.map(({ item: linkItem, indexPath: linkIndexPath }) => {
                          const linkKey =
                            linkItem?.key ||
                            linkItem?.path ||
                            `${linkItem?.label || "nav-item"}-${linkIndexPath}`;
                          return React.cloneElement(
                            renderNavLink(linkItem, linkIndexPath, "commercehub-products-mega-link"),
                            { key: `${linkKey}-${linkIndexPath}` }
                          );
                        })
                      : renderNavLink(child, childIndexPath, "commercehub-products-mega-link")}
                  </div>
                </div>
              );
            })}
            <div className={`commercehub-products-column${isOthersActive ? " active" : ""}`}>
              <h4 className="commercehub-products-column-title">{t("productsOthers")}</h4>
              <div className="commercehub-products-column-links">
                <Link
                  to="/products?group=Others"
                  className={`commercehub-products-mega-link${isOthersActive ? " active" : ""}`}
                  onClick={(event) => {
                    dismissNavFocus();
                    handleNavClick(event, "/products?group=Others");
                  }}
                >
                  {t("productsOthers")}
                </Link>

              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderNavItems(items, depth = 1, parentIndex = "") {
    if (!Array.isArray(items) || !items.length) {
      return null;
    }
    return items.filter((item) => item.visible !== false).map((item, index) => {
      const indexPath = parentIndex ? `${parentIndex}-${index}` : String(index);
      const visibleChildren = getVisibleChildren(item);
      const hasChildren = visibleChildren.length > 0;
      const parentActive = isNavBranchActive(item, indexPath);
      const navKey = item.key || `${item.label}-${item.path}-${indexPath}`;
      const isProductsRoot = depth === 1 && String(item.key || "").trim() === "products";

      if (!hasChildren) {
        return React.cloneElement(renderNavLink(item, indexPath), { key: navKey });
      }

      return (
        <div
          key={navKey}
          className={`commercehub-nav-item has-children nav-depth-${depth}${parentActive ? " active" : ""}${isProductsRoot && productsMenuOpen ? " is-open" : ""}`}
          onMouseEnter={isProductsRoot ? openProductsMenu : undefined}
          onMouseLeave={isProductsRoot ? scheduleProductsMenuClose : undefined}
          onFocusCapture={isProductsRoot ? openProductsMenu : undefined}
          onBlurCapture={isProductsRoot ? handleProductsMenuBlur : undefined}
        >
          {renderNavLink(item, indexPath, parentActive ? "active" : "")}
          <span className="commercehub-nav-caret" aria-hidden="true">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 10l5 5 5-5z" />
            </svg>
          </span>
          {isProductsRoot
            ? renderProductsPanel(item, indexPath)
            : (
              <div className="commercehub-submenu" role="menu" aria-label={`${item.label} submenu`}>
                {renderNavItems(visibleChildren, depth + 1, indexPath)}
              </div>
            )}
        </div>
      );
    });
  }

  // LOGO区
  // 右侧查找按钮弹窗逻辑
  const apiBaseUrl = process.env.REACT_APP_API_URL || "/api";
  const activeLanguageOption =
    STOREFRONT_LANGUAGE_OPTIONS.find((option) => option.value === String(language || "en")) ||
    STOREFRONT_LANGUAGE_OPTIONS[0];

  return (
    <>
      <header className="container store-header commercehub-header">
        <Link to="/" className="loya-brand-lockup loya-logo-link" aria-label={t("ariaBackToHomepage")}>
          <img className="loya-brand-logo" src={logoSrc} alt={logoAlt || storefrontTitle || "Store logo"} />
          <div className="commercehub-wordmark">
            <strong className="brand-main"><span className="loya-art-font">LOYA</span></strong>
            <span className="brand-sub">SPORT</span>
          </div>
        </Link>
        <div className="commercehub-header-mobile-toggle">
          <button
            type="button"
            className={`hamburger-menu ${mobileMenuOpen ? "is-active" : ""}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={t("ariaToggleNavigationMenu")}
            aria-expanded={mobileMenuOpen}
            aria-controls="commercehub-primary-nav"
          >
            <div className="hamburger-line"></div>
            <div className="hamburger-line"></div>
            <div className="hamburger-line"></div>
          </button>
        </div>
        <nav
          id="commercehub-primary-nav"
          className={`commercehub-main-nav ${mobileMenuOpen ? "is-open" : ""}`}
          aria-label={t("ariaPrimaryNavigation")}
        >
          {renderNavItems(navItems)}
        </nav>
        <div className="commercehub-header-tools">
          <div className="commercehub-language-switch">
            <div className={`commercehub-language-dropdown${languageMenuOpen ? " is-open" : ""}`} ref={languageMenuRef}>
              <button
                type="button"
                className="commercehub-language-trigger"
                aria-label={t("ariaSwitchStorefrontLanguage")}
                aria-haspopup="listbox"
                aria-expanded={languageMenuOpen}
                onClick={() => setLanguageMenuOpen((prev) => !prev)}
              >
                <span className="commercehub-language-flag" aria-hidden="true">
                  {renderLanguageFlagIcon(activeLanguageOption?.value)}
                </span>
                <span className="commercehub-language-trigger-text">{activeLanguageOption?.label}</span>
                <span className="commercehub-language-caret" aria-hidden="true">▾</span>
              </button>
              <div className="commercehub-language-menu" role="listbox" aria-label={t("ariaSwitchStorefrontLanguage")}>
                {STOREFRONT_LANGUAGE_OPTIONS.map((option) => {
                  const selected = option.value === activeLanguageOption?.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`commercehub-language-option${selected ? " is-selected" : ""}`}
                      onClick={() => {
                        if (typeof onChangeLanguage === "function") {
                          onChangeLanguage(option.value);
                        }
                        setLanguageMenuOpen(false);
                      }}
                    >
                      <span className="commercehub-language-flag" aria-hidden="true">
                        {renderLanguageFlagIcon(option.value)}
                      </span>
                      <span>{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          {isAuthenticated ? (
            <Link className="commercehub-login-trigger" to="/admin" aria-label={t("ariaAccountCenter")}>{t("btnAccount")}</Link>
          ) : (
            <Link className="commercehub-login-trigger" to="/login" aria-label={t("ariaLogin")}>{t("btnLogin")}</Link>
          )}
          <Link className="button quote commercehub-contact-button" to="/contact">{t("requestQuote")}</Link>
          <div className="commercehub-icon-group">
            <button
              type="button"
              className="commercehub-icon commercehub-icon-button"
              aria-label={t("ariaSearchProductByName")}
              title={t("ariaSearch")}
              onClick={() => setSearchOpen(true)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </button>
          </div>
        </div>
      </header>
      <ProductSearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} apiBaseUrl={apiBaseUrl} />
    </>
  );
}
